const getLocaleAmountParts = () => {
  const userLocale = navigator.language;
  const parts = new Intl.NumberFormat(userLocale).formatToParts(1234567.89);
  const decimalSep = parts.find((p) => p.type === 'decimal')?.value ?? '.';
  const groupSep = parts.find((p) => p.type === 'group')?.value ?? ',';
  return { userLocale, decimalSep, groupSep };
};

/**
 * Format an amount in cents as a locale-aware decimal string (no currency symbol).
 * e.g. 100023 → "1,000.23" (en-US) or "1.000,23" (de-DE)
 */
export const formatAmountFromCents = (cents: number): string => {
  const { userLocale } = getLocaleAmountParts();
  return new Intl.NumberFormat(userLocale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
};

/**
 * Parse a locale-formatted amount string back to cents.
 * Handles locale-specific decimal and group separators.
 */
export const parseAmountToCents = (value: string): number => {
  if (!value) return 0;
  return Math.round(parseFloat(value.replaceAll(',', '.')) * 100) || 0;
};

/**
 * Strip locale group separators from an amount string so the user can type cleanly.
 * e.g. "1.000,23" → "1000,23" (de-DE)
 */
export const stripAmountGroupSeparators = (value: string): string => {
  const { groupSep } = getLocaleAmountParts();
  return value.split(groupSep).join('');
};

/**
 * Returns true if the string is a valid locale-formatted amount (or empty).
 */
export const isValidAmountInput = (value: string): boolean => {
  if (!value || value.trim() === '') return false;
  return /^-?\d+([.,]\d+)?$/.test(value.trim());
};

export const toCurrency = (
  value: number,
  currency_code: string | null = null,
  fractionDigits: number = 0,
): string => {
  if (value === undefined) {
    return '';
  }
  if (isNaN(value)) {
    return String(value);
  }
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency_code || 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return formatter.format(value / 100);
};

export const centsToCurrencyShorthand = (
  valueInCents: number,
  fractionDigits: number = 0,
): string => {
  if (valueInCents === undefined) {
    return '';
  }
  const isNegative = valueInCents < 0;
  const valueInDollars = Math.abs(valueInCents) / 100;

  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'm' },
  ];

  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const item = lookup
    .slice()
    .reverse()
    .find((entry) => valueInDollars >= entry.value);

  const result = item
    ? (valueInDollars / item.value).toFixed(fractionDigits).replace(rx, '$1') +
      item.symbol
    : '0';

  return isNegative ? `-${result}` : result;
};
